import MuiAvatar, { type AvatarProps as MuiAvatarProps } from '@mui/material/Avatar';

import { cssVar } from '../../theme/tokens';
import { type ControlSize, mergeSx } from './sizes';

export interface AvatarProps extends Omit<MuiAvatarProps, 'children' | 'alt'> {
  /** The person's name: the image alt text and the source of the initials. */
  name: string;
  /** Photo URL; initials are shown without one (or when it fails to load). */
  src?: string;
  /** Same heights as the controls: sm 32, md 40, lg 48. */
  size?: ControlSize;
  /** Decorative next to the visible name: hide it from assistive technology. */
  decorative?: boolean;
}

/** Up to two initials: first and last word ("Thandi van Wyk" → "TW"). */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const first = words[0]?.[0] ?? '';
  const last = words.length > 1 ? (words.at(-1)?.[0] ?? '') : '';
  return `${first}${last}`.toUpperCase() || '?';
}

/** A person's photo or initials (gold with navy text, both schemes). */
export function Avatar({ name, src, size = 'md', decorative = false, sx, ...props }: AvatarProps) {
  const dimension = cssVar(`control-h-${size}`);
  return (
    <MuiAvatar
      src={src}
      alt={decorative ? '' : name}
      aria-hidden={decorative || undefined}
      role={decorative || src ? undefined : 'img'}
      aria-label={decorative || src ? undefined : name}
      sx={mergeSx(
        {
          width: dimension,
          height: dimension,
          bgcolor: cssVar('primary'),
          color: cssVar('text-on-primary'),
          fontSize: `calc(${dimension} * 0.4)`,
          fontWeight: 700,
        },
        sx,
      )}
      {...props}
    >
      {initials(name)}
    </MuiAvatar>
  );
}
